import { findAvailableLotMatch, resolveCanonicalLotKey } from './lotIdentity'

export type LotLike = {
  id: string
  lotKey?: string | null
}

export type LotScopedRow = {
  lotId?: string | null
  lot?: string | null
}

/** A `(farmId, productionUnitId)` overlay bucket plus the lotIds its production rows claim. */
export type LotBucketScope = {
  farmId: string
  productionUnitId: string
  lotIds: ReadonlySet<string>
}

export type LocationScopedLog = {
  farmId?: string | null
  productionUnitId?: string | null
  lotId?: string | null
}

/**
 * A1.1 Risk #2 fix: route an event log to an overlay bucket by `lotId`
 * when possible, falling back to the log's recorded location otherwise.
 *
 * Location-only scoping drops every log belonging to a transferred/moved
 * lot from that lot's *other* location buckets: the log legitimately
 * carries the location where the event happened, so a bucket holding the
 * lot's rows from a different location never sees it and the overlay
 * silently reverts those cells to their raw stored values.
 *
 * `lotId` is globally unique, so it is strictly more precise than
 * location — but only when some production row actually claims it
 * (`attributableLotIds`). An unclaimed lotId (log for a lot with no rows
 * in this dataset, or a stale/foreign id) would route the log to no
 * bucket at all, so those fall back to the legacy location match, as do
 * legacy logs written before the `lotId` column existed.
 *
 * The legacy fallback is what preserves anti-collision behavior for
 * label-matched overlays: two farms reusing the same lot label on the
 * same day still keep their logs apart, because neither the lotId branch
 * (distinct ids) nor the location branch (distinct farmIds) crosses over.
 */
export const scopeLogsToLotBucket = <T extends LocationScopedLog>(
  logs: readonly T[],
  bucket: LotBucketScope,
  attributableLotIds: ReadonlySet<string>
): T[] => logs.filter((log) => {
  const lotId = String(log.lotId ?? '').trim()
  if (lotId && attributableLotIds.has(lotId)) return bucket.lotIds.has(lotId)

  return String(log.farmId ?? '') === bucket.farmId
    && String(log.productionUnitId ?? '') === bucket.productionUnitId
})

const resolveScopedLotId = (value: string, lots: LotLike[]): string | null => {
  const match = findAvailableLotMatch(value, lots)
  return match.status === 'matched' ? match.match.id : null
}

export const filterRowsToLots = <T extends LotScopedRow>(rows: T[], lots: LotLike[]): T[] => {
  const validLotIds = new Set(lots.map((lot) => String(lot.id ?? '').trim()).filter(Boolean))

  if (validLotIds.size === 0 && lots.length === 0) return rows

  return rows.filter((row) => {
    const rowLotId = String(row.lotId ?? '').trim()
    const rowLotKey = String(row.lot ?? '').trim()

    if (rowLotId) {
      if (validLotIds.has(rowLotId)) return true

      const resolvedLotId = resolveScopedLotId(rowLotId, lots)
      if (!rowLotKey) {
        return Boolean(resolvedLotId)
      }

      const resolvedLotKeyId = resolveScopedLotId(rowLotKey, lots)
      if (!resolvedLotKeyId) return false
      if (resolvedLotId) return resolvedLotId === resolvedLotKeyId

      return resolveCanonicalLotKey(rowLotId) === resolveCanonicalLotKey(rowLotKey)
    }

    if (!rowLotKey) return false
    return findAvailableLotMatch(rowLotKey, lots).status === 'matched'
  })
}

const hasWellFormedLotIdentity = <T extends LotScopedRow>(row: T): boolean => {
  const rowLotId = String(row.lotId ?? '').trim()
  const rowLotKey = String(row.lot ?? '').trim()
  return Boolean(rowLotId || rowLotKey)
}

/**
 * U3 (docs/lot-lifecycle-vs-concerns-audit.md §2 M-Y12): companion to
 * `filterRowsToLots` that partitions rows into `active` (current roster —
 * byte-identical to `filterRowsToLots`'s output) and `historical` (rows
 * that fail the roster match but still carry a well-formed `lotId`/`lot`
 * identity — moved-out lots, soft-deleted lots, etc.).
 *
 * `filterRowsToLots` drops `historical` rows entirely, which silently
 * zeroes out biomass for lots that moved out of the current production
 * unit or were soft-deleted after accruing production history. Callers
 * that need to keep surfacing that history (e.g. a "Histórico" KPI
 * breakdown) should use this instead; `filterRowsToLots` itself stays
 * byte-identical for callers that only need the active roster.
 *
 * Rows with neither a `lotId` nor a `lot` label are garbage in either
 * direction and are excluded from both partitions, mirroring
 * `filterRowsToLots`'s existing behavior at the bottom of its filter.
 *
 * The empty-roster safety net (`validLotIds.size === 0 && lots.length ===
 * 0`, meaning no production unit is selected yet) is preserved: every row
 * goes to `active`, `historical` stays empty.
 */
export const partitionRowsByLotRoster = <T extends LotScopedRow>(
  rows: T[],
  lots: LotLike[]
): { active: T[]; historical: T[] } => {
  const validLotIds = new Set(lots.map((lot) => String(lot.id ?? '').trim()).filter(Boolean))

  if (validLotIds.size === 0 && lots.length === 0) {
    return { active: rows, historical: [] }
  }

  const active = filterRowsToLots(rows, lots)
  const activeRowSet = new Set<T>(active)
  const historical = rows.filter((row) => !activeRowSet.has(row) && hasWellFormedLotIdentity(row))

  return { active, historical }
}
